import React, { useState } from "react";
import styled from "styled-components";
import PatientSidebar from "components/organisms/PatientSidebar";
import CommonHeader from "components/organisms/CommonHeader";
import AddPatientModal from "components/molecules/AddPatientModal";
import { Outlet, useNavigate, useLocation, useParams } from "react-router-dom";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { log, LogLevel } from "utils/LogUtil";
import { savePatient } from "api/patientService";
import { EMPTY_PATIENT } from "common/models/types";
import { useDispatch } from "react-redux";
import { AppDispatch } from "store";
import { fetchPatientList } from "store/middleware";
import { eventLog } from "utils/AmplitudeUtil";
import { withPageGuard } from "components/common/withPageGuard";

const PageContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const StyledCommonHeader = styled(CommonHeader)`
  flex-shrink: 0;
`;

const LayoutContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const ContentContainer = styled.div`
  flex: 1;
  overflow: auto;
  min-height: 0;
`;

const Button = styled.button`
  background: #1976d2;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  margin-left: 8px;
`;

const PatientsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId, reviewId } = useParams();
  const numericPatientId = React.useMemo(() => (patientId ? Number(patientId) : null), [patientId]);
  const numericReviewId = React.useMemo(() => (reviewId ? Number(reviewId) : null), [reviewId]);
  const dispatch = useDispatch<AppDispatch>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Sidebar에서 환자 선택 시 URL 이동
  const handlePatientSelect = (patientId: number) => {
    const nextLocation = `/patients/${patientId}`;
    
    log(LogLevel.INFO, "[PatientsPage] 환자 선택", {
      patientId,
      nextLocation,
      isInEditMode: !!(window as any).__isInEditMode,
    });
    
    // 편집 모드 체크 - 간단한 confirm 사용
    const isInEditMode = (window as any).__isInEditMode;
    if (isInEditMode) {
      const confirmResult = window.confirm(
        "편집 중인 내용이 있습니다.\n\n정말 이 페이지를 벗어나시겠습니까?"
      );
      
      if (!confirmResult) {
        log(LogLevel.INFO, "[PatientsPage] 사용자가 네비게이션 취소");
        return;
      }
      
      // 사용자가 확인했으면 상태 초기화
      log(LogLevel.INFO, "[PatientsPage] 사용자가 네비게이션 확인");
      (window as any).__isInEditMode = false;
    }
    
    // 네비게이션 실행
    eventLog("환자선택", { patientId });
    navigate(nextLocation);
  };

  const handleReportSelect = (patientId: number, reportId: number) => {
    const nextLocation = `/patients/${patientId}/reviews/${reportId}`;
    
    log(LogLevel.INFO, "[PatientsPage] 보고서 선택", {
      patientId,
      reportId,
      nextLocation,
      isInEditMode: !!(window as any).__isInEditMode,
    });
    
    // 편집 모드 체크 - 간단한 confirm 사용
    const isInEditMode = (window as any).__isInEditMode;
    if (isInEditMode) {
      const confirmResult = window.confirm(
        "편집 중인 내용이 있습니다.\n\n정말 이 페이지를 벗어나시겠습니까?"
      );
      
      if (!confirmResult) {
        log(LogLevel.INFO, "[PatientsPage] 사용자가 네비게이션 취소");
        return;
      }
      
      // 사용자가 확인했으면 상태 초기화
      log(LogLevel.INFO, "[PatientsPage] 사용자가 네비게이션 확인");
      (window as any).__isInEditMode = false;
    }
    
    // 네뺄게이션 실행
    eventLog("리포트선택", { patientId, reportId });
    navigate(nextLocation);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCreatePatient = async (patientData: {
    birthYear: string;
    gender: string;
    memo: string;
  }) => {
    try {
      log(LogLevel.INFO, "handleCreatePatient", patientData);

      WaitingManager.show({
        message: "환자 정보를 저장하는 중입니다...",
      });

      // 출생일 계산 (출생년도의 1월 1일로 설정)
      const birthDate = new Date(parseInt(patientData.birthYear), 0, 1).toISOString();

      // 새 환자 객체 생성
      const newPatient = {
        ...EMPTY_PATIENT,
        birthDate: birthDate,
        gender: patientData.gender,
        memo: patientData.memo,
      };

      const response = await savePatient(newPatient);
      log(LogLevel.VERBOSE, "handleCreatePatient response", response);

      if (response.error) {
        throw new Error(response.message || "환자 정보를 저장하는 데 실패했습니다.");
      }

      const patientId = response.data?.id;
      if (patientId) {
        dispatch(fetchPatientList({ forceRefresh: true }));
        eventLog("환자등록", { patientId });

        // 성공 토스트 메시지 표시
        ToastManager.show({
          content: "새 환자가 생성되었습니다.",
          duration: 3000,
        });

        navigate(`/patients/${patientId}`, { replace: true, state: { edit: true } });
      } else {
        throw new Error("환자 정보를 저장하는 데 실패했습니다.");
      }
    } catch (error) {
      log(LogLevel.ERROR, "handleCreatePatient error", error);
      ToastManager.show({
        content: error instanceof Error ? error.message : "환자를 생성하는 데 실패했습니다.",
        duration: 3000,
      });
    } finally {
      WaitingManager.hide();
      setIsModalOpen(false);
    }
  };

  const handleLogout = () => {
    navigate("/login");
  };

  return (
    <>
      <PageContainer>
        <StyledCommonHeader />
        <LayoutContainer>
          <PatientSidebar
            onPatientSelect={handlePatientSelect}
            selectedPatientId={numericPatientId}
            selectedReportId={numericReviewId}
            onSelectReport={handleReportSelect}
            onAddPatient={handleOpenModal}
          />
          <ContentContainer>
            <Outlet />
          </ContentContainer>
        </LayoutContainer>
      </PageContainer>
      <AddPatientModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleCreatePatient}
      />
    </>
  );
};

export default withPageGuard(PatientsPage, {
  requireAuth: true,
  requireAgreement: true,
  requireGuide: true,
});
